import { create } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';
import { GPUInstance } from '@/service/gpu-instance/type';

type State = {
  token: string | null;
  loading: boolean;
  initialized: boolean;
  instances: GPUInstance[];
  currentInstance: GPUInstance | null;
};

type Action = {
  setToken: (token: State['token']) => void;
  setInstances: (instances: GPUInstance[]) => void;
  setCurrentInstance: (instance: GPUInstance | null) => void;
  addInstance: (instance: GPUInstance) => void;
  updateInstance: (instanceId: string, updates: Partial<GPUInstance>) => void;
  removeInstance: (instanceId: string) => void;
};

type GPUInstanceStore = State & Action;

type GPUInstancePersist = {
  token: string | null;
};

const persistOptions: PersistOptions<GPUInstanceStore, GPUInstancePersist> = {
  name: 'gpu-instance-storage',
  partialize: (state) => ({ token: state.token }),
};

export const useGPUInstanceStore = create<GPUInstanceStore>()(
  persist(
    (set) => ({
      token: null,
      loading: true,
      initialized: false,
      instances: [],
      currentInstance: null,
      setToken: (token) =>
        set(() => ({
          token,
          initialized: true,
          loading: false,
        })),
      setInstances: (instances) => set({ instances }),
      setCurrentInstance: (instance) => set({ currentInstance: instance }),
      addInstance: (instance) =>
        set((state) => ({
          instances: [...state.instances, instance],
        })),
      updateInstance: (instanceId, updates) =>
        set((state) => ({
          instances: state.instances.map((inst) =>
            inst.task_id === instanceId ? { ...inst, ...updates } : inst
          ),
          currentInstance:
            state.currentInstance?.task_id === instanceId
              ? { ...state.currentInstance, ...updates }
              : state.currentInstance,
        })),
      removeInstance: (instanceId) =>
        set((state) => ({
          instances: state.instances.filter((inst) => inst.task_id !== instanceId),
          currentInstance:
            state.currentInstance?.task_id === instanceId
              ? null
              : state.currentInstance,
        })),
    }),
    persistOptions
  )
);
